import React, { useEffect, useRef, useState } from "react";
import TitleBar from "../../components/common-components/TitleBar";
import { Box, CircularProgress, Typography } from "@mui/material";
import CustomBackButton from "../../components/common-components/CustomBackButton";
import CustomButton from "../../components/common-components/CustomButton";
import { generatePDF, generatePDFd } from "../../utils/StorageUtils";
import SS4Header from "../../components/ss4container/SS4Header";
import { SS4_CAPTURE_WIDTH_PX } from "../../components/ss4container/ss4-geometry";
import img3 from "../../assets/Images/image3.png";
import img4 from "../../assets/Images/image4.png";
import CommonDeatails from "../../components/common-components/CommonDetails";
import { useAppDispatch } from "../../stores/store";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  createIncorpartion,
  getIncorporationList,
  updateIncorpartion,
} from "../../stores/incorporation";
import { createUpdateItinW7 } from "../../stores/itin";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { setToast } from "../../stores/toast/toastslice";
import CustomButtonFormStep from "../../components/common-components/CustomButtonStepperForm";

interface ListItemData {
  img: string;
  text: string;
}

interface SS4FormProps {
  incorporationDetails?: any;
  handleClose?: () => void;
}

const SS4Form = ({ handleClose }: SS4FormProps) => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const handleCloseForm = handleClose || (() => {});
  const location = useLocation();
  const isEdit = location.pathname.includes("edit");
  const navigate = useNavigate();
  const [getLoading, setGetLoading] = useState(false);
  const { id } = useParams<{ id: string }>();
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const { incorporationDetails } = useSelector(
    (store: any) => store.incorporation,
  );

  const FetchList = async () => {
    await dispatch<any>(getIncorporationList({ order_id: id }));
  };
  useEffect(() => {
    FetchList();
  }, []);

  const [ss4FormValues, setSs4FormValues] = useState<any>({
    ...incorporationDetails,
  });
  

  const handleUpdate = (values: any) => {
    setSs4FormValues((prev: any) => ({
      ...prev,
      ...values,
    }));
  };

  useEffect(() => {
    console.log("Updated ss4FormValues:", ss4FormValues);
  }, [ss4FormValues]);

  // A4, in mm.
  // US Letter, the official Form SS-4 sheet. Each #pdf-content-page-* div is
  // laid out at exactly 611.976 x 791.968pt with the official's 36pt margins
  // already inside the artwork, so a section maps onto a PDF page 1:1 and
  // edge-to-edge. Adding a margin here would double the official's.
  const pageWidth = 215.9;
  const pageHeight = 279.4;
  /** Kept clear of the sheet edge so nothing lands in a printer's dead zone. */

  /**
   * The SS4 renders as two sections — the form, and the "Do I Need an EIN?"
   * reference block — and each section is already a complete US Letter sheet
   * with the form's own 36pt margin baked in (see ss4-geometry). So a section
   * maps to exactly one PDF page and must be placed whole.
   *
   * This used to slice each canvas by pixel height against a 230mm page. Once
   * the sections became full letter sheets that arithmetic started overflowing:
   * a 2100x2717 section captures at 4200x5434, but the slice height worked out
   * to 4863px, so every section spilled a second page holding the last ~570px.
   * That is where the stray 2nd and 4th pages came from.
   */
  const generatePDF = async (): Promise<Blob> => {
    const sectionIds = ["pdf-content-page-1", "pdf-content-page-2"];
    const pdf = new jsPDF("p", "mm", "letter", true);
    let addedPage = false;

    // The form is displayed shrunk (`zoom` on .ss4-screen, see ss4_form.css) so
    // it fits a viewport. html2canvas doesn't reliably honour zoom, and the
    // PDF wants the full-size geometry anyway, so undo it for the capture.
    // setProperty/removeProperty rather than `style.zoom`: zoom is absent from
    // CSSStyleDeclaration in this TS lib version.
    const root = document.getElementById("pdf-content");
    const previousZoom = root ? root.style.getPropertyValue("zoom") : "";

    // Resetting zoom to 1 below visibly grows the live element. A
    // fixed-position spinner overlay covers it for the duration (rendered
    // below) — same treatment as the Form 2848/W-7 captures. Note: this must
    // stay a plain position:fixed overlay and NOT clip the wrapper itself
    // (e.g. via overflow:hidden) — html2canvas respects ancestor overflow
    // clipping, so constraining the live element's ancestor box during
    // capture would crop the rendered canvas instead of just hiding it
    // visually.
    setCapturing(true);

    if (root) {
      root.style.setProperty("zoom", "1");
      // reveals the print-only page-2 running head for the capture
      root.classList.add("is-capturing");
    }

    try {
      for (const sectionId of sectionIds) {
        const input = document.getElementById(sectionId);
        if (!input) continue;

        const w = `${SS4_CAPTURE_WIDTH_PX}px`;
        input.style.width = w;
        input.style.minWidth = w;
        input.style.maxWidth = w;

        const canvas = await html2canvas(input, { scale: 2, useCORS: true });

        if (addedPage) pdf.addPage();
        pdf.addImage(
          canvas.toDataURL("image/png"),
          "PNG",
          0,
          0,
          pageWidth,
          pageHeight,
          undefined,
          "FAST",
        );
        addedPage = true;
      }
    } finally {
      // restore the on-screen scale even if a capture throws; dropping the
      // inline value entirely hands control back to the .ss4-screen rule
      if (root) {
        if (previousZoom) root.style.setProperty("zoom", previousZoom);
        else root.style.removeProperty("zoom");
        root.classList.remove("is-capturing");
      }
      setCapturing(false);
    }

    if (!addedPage) throw new Error("PDF content not found!");

    return pdf.output("blob");
  };

  const mergePayload = async (): Promise<FormData> => {
    const pdfBlob = await generatePDF();
    const pdfFile = new File([pdfBlob], "document.pdf", {
      type: "application/pdf",
    });

    const sizeInKB = (pdfFile.size / 1024).toFixed(2);
    const sizeInMB = (pdfFile.size / (1024 * 1024)).toFixed(2);
    console.log(`📄 Compressed PDF Size: ${sizeInKB} KB (${sizeInMB} MB)`);

    // const { extracted_data, ...filteredSs4FormValues } = ss4FormValues;
    const { ss4_extracted_data, extracted_data, user, ein_form_file, ...rest } =
      ss4FormValues;

    // const cleanedSs4FormValues = Object.entries(filteredSs4FormValues).reduce(
    //   (acc, [key, value]) => {
    //     if (value !== undefined && value !== null && value !== "") {
    //       acc[key] = value;
    //     }
    //     return acc;
    //   },
    //   {} as Record<string, any>
    // );

    const formData = new FormData();
    formData.append("ss4_extracted_data", JSON.stringify(rest));
    // formData.append("ss4_extracted_data", JSON.stringify(cleanedSs4FormValues));
    formData.append("ein_form_file", pdfFile);
    formData.append("incorporation_id", incorporationDetails?.id ?? "");
    if (isEdit) {
      formData.append("is_updating", "updated");
    }
    return formData;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setIsSubmit(true);
    e.preventDefault();
    setLoading(true);

    try {
      const formData = await mergePayload();

      console.log("🚀 FormData before sending:", formData);

      const response = await dispatch<any>(
        updateIncorpartion(formData),
      ).unwrap();

      if (response?.status === 1) {
        setLoading(false);
        dispatch<any>(
          setToast({
            message: response?.message || "Form submitted successfully!",
            type: "success",
            title: "Success",
          }),
        );

        setTimeout(() => {
          navigate(`/incorporation-detail/${id}`);
        }, 100);
      } else {
        setLoading(false);
        dispatch<any>(
          setToast({
            message: response?.message || "Something went wrong!",
            type: "error",
            title: "Error",
          }),
        );
      }
      setIsSubmit(false);
    } catch (error: any) {
      setLoading(false);
      dispatch<any>(
        setToast({
          message: "Submission failed. Please try again.",
          type: "error",
          title: "Error",
        }),
      );
      setIsSubmit(false);
    }
  };
  return (
    <>
      <form onSubmit={handleSubmit}>
        <>
          <TitleBar
            title={"Fill the SS4 form"}
            description={"Complete the SS4 form to apply for an EIN"}
          />

          <Box ref={wrapperRef} sx={{ position: "relative" }}>
            <SS4Header
              formValue={incorporationDetails}
              setFormValues={handleUpdate}
              loading={loading}
              isSubmit={isSubmit}
            />
            {capturing && (
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  bottom: 0,
                  // only covers the content column to the right of the
                  // sidebar, not the full viewport, so it stays visible
                  left: wrapperRef.current?.getBoundingClientRect().left ?? 0,
                  right: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  bgcolor: "#fff",
                  zIndex: 1300,
                }}
              >
                <CircularProgress />
                <Typography variant="body1" color="text.secondary">
                  Preparing your form…
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              marginTop: 2,
              display: "flex",
              justifyContent: "flex-start",
              gap: "20px",
            }}
          >
            <CustomButtonFormStep
              btnTitle="Back"
              disabled={loading}
              // handleClick={handleClose}
              handleClick={() => navigate(-1)}
              className="me-[10px]"
              bgColor="#EAF1FA"
              textColor={"#2977CC"}
            />
            <CustomButtonFormStep
              btnTitle={isEdit ? "Update" : "Submit"}
              minwidth="191px"
              type="submit"
              loading={loading}
            />
          </Box>
        </>
      </form>
    </>
  );
};

export default SS4Form;
